import { GridBreak, RichTextReader, Typography } from "@compass/core";
import {
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  FormControl,
  FormLabel,
  Radio,
  Grid,
  useMediaQuery,
  Stack,
  Box,
  Checkbox,
} from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { Input } from "components";
import { ApplicationQuestion, FormQuestion } from "generated/openApi";
import { FormQuestionTypes } from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { TransientFormAnswer } from "./TransientQuestionResponse";
import { MatrixOption, MatrixQuestion } from "./MatrixGroup";

export type DoubleMatrixGroupProps = MuiRadioGroupProps & {
  formQuestion?: FormQuestion;
  isDisabled: boolean;
  values: TransientFormAnswer[];
  handleChange: (value: TransientFormAnswer, clicked?: boolean) => any;
  subQuestions: MatrixQuestion[];
  options: MatrixOption[];
};

export const DoubleMatrixGroup = ({
  formQuestion,
  isDisabled,
  values,
  subQuestions,
  options,
  handleChange,
}: DoubleMatrixGroupProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  // default question size
  let subQuestionSize: number = 3;

  if (formQuestion && formQuestion.questionWidthPercent != undefined && formQuestion.questionWidthPercent) {
    // % values do not mix well with grid, so using some ranges
    if (formQuestion.questionWidthPercent < 25) subQuestionSize = 2;
    else if (formQuestion.questionWidthPercent >= 25 && formQuestion.questionWidthPercent < 40) subQuestionSize = 4;
    else if (formQuestion.questionWidthPercent >= 40 && formQuestion.questionWidthPercent < 50) subQuestionSize = 4;
    else if (formQuestion.questionWidthPercent >= 50 && formQuestion.questionWidthPercent < 60) subQuestionSize = 6;
    else if (formQuestion.questionWidthPercent >= 60 && formQuestion.questionWidthPercent < 68) subQuestionSize = 6;
    else if (formQuestion.questionWidthPercent >= 68 && formQuestion.questionWidthPercent < 75) subQuestionSize = 8;
    else if (formQuestion.questionWidthPercent >= 75) subQuestionSize = 8;
  }

  const remainingColumns: number = 12 - subQuestionSize;
  const optionSize: number = Math.floor(remainingColumns / options.length);
  const mobileOptionSize: number = Math.floor(12 / options.length);

  const onHandleChange = (optionValue: any, question: MatrixQuestion) => {
    var selectedValue = optionValue.target.value;
    const newValue = getValue(question)?.toString() === selectedValue ? null : selectedValue;

    handleChange({
      subQuestionId: question.subQuestionId,
      formAnswerId: parseInt(newValue),
    });
  };

  const getValue = (question: MatrixQuestion): number | null => {
    const value = values.find((v) => v.subQuestionId == question.subQuestionId);
    if (value) return value?.formAnswerId!;
    return null;
  };

  const renderRow = (subQuestion: MatrixQuestion, index: number) => {
    let branchedSubQuestion: MatrixQuestion | undefined = subQuestions.find(
      (sq) => subQuestion.subQuestionId == sq.branchedFromSubQuestionId
    );

    return (
      <Grid
        item
        xs={12}
        sx={{
          paddingY: 1,
          background: index % 2 && formQuestion?.isAlternatingRowColor ? "#f5f5f5" : "white",
        }}
      >
        <Grid container spacing="sm">
          <Grid item xs={remainingColumns / 2}>
            <MuiRadioGroup
              row
              key={`${index}:left`}
              aria-labelledby="radio-buttons-group"
              name="controlled-radio-buttons-group-left"
              value={getValue(subQuestion)}
            >
              <Stack direction="row" spacing="sm" width="100%">
                {options.map((option, index) => (
                  <div key={index} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    <FormControlLabel
                      sx={{ margin: 0 }}
                      value={option.formAnswerId}
                      control={<Radio onClick={(e) => onHandleChange(e, subQuestion)} />}
                      label=""
                      disabled={isDisabled}
                    />
                  </div>
                ))}
              </Stack>
            </MuiRadioGroup>
          </Grid>
          <Grid item xs={subQuestionSize}>
            <Stack direction="row" spacing={2}>
              <RichTextReader
                customStyles={{
                  div: {
                    textAlign: "center",
                  },
                }}
                html={subQuestion.label}
              />
              <RichTextReader html={subQuestion.question} />
            </Stack>
          </Grid>
          <Grid item xs={remainingColumns / 2}>
            <MuiRadioGroup
              row
              key={`${index}:right`}
              aria-labelledby="radio-buttons-group"
              name="controlled-radio-buttons-group-right"
              value={getValue(branchedSubQuestion!)}
            >
              <Stack direction="row" spacing="sm" width="100%">
                {options.map((option, index) => (
                  <div key={index} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    <FormControlLabel
                      sx={{ margin: 0 }}
                      value={option.formAnswerId}
                      control={<Radio onClick={(e) => onHandleChange(e, branchedSubQuestion!)} />}
                      label=""
                      disabled={isDisabled}
                    />
                  </div>
                ))}
              </Stack>
            </MuiRadioGroup>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSurveyDesktopView = () => {
    let verticalNonsense = {
      // textAlign: "center",
      padding: 1,
      overflow: "hidden",
      wordBreak: "break-word", // Ensures long words break
      hyphens: "auto", // Adds hyphenation support
      writingMode: "vertical-rl", // Vertical text
      textOrientation: "mixed", // Proper character rendering
      transform: "rotate(180deg)", // Reverse text order
      maxHeight: "120px", // Limits height so text wraps
      whiteSpace: "normal", // Allows wrapping
    };
    let horizontalStyle = {
      textAlign: "center",
      padding: 1,
      overflow: "hidden", // Ensure text doesn't overflow
      wordBreak: "break-word", // Break long words
      textOrientation: "mixed",
    };

    return (
      <FormControl style={{ width: "100%" }}>
        <Grid container spacing="sm">
          <Grid item xs={remainingColumns / 2}>
            <RichTextReader
              html={formQuestion?.alternativeText1}
              customStyles={{
                div: {
                  marginBottom: "1em",
                  marginTop: "1em",
                },
              }}
            />
          </Grid>

          <Grid item xs={subQuestionSize} />

          <Grid item xs={remainingColumns / 2}>
            <RichTextReader
              html={formQuestion?.alternativeText2}
              customStyles={{
                div: {
                  marginBottom: "1em",
                  marginTop: "1em",
                },
              }}
            />
          </Grid>

          <GridBreak />

          {/* Labels */}
          <Grid item xs={remainingColumns / 2}>
            <Stack direction="row" spacing="sm">
              {options.map((o, i) => {
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "end",
                      hyphens: "auto",
                      overflow: "hidden", // Ensure text doesn't overflow
                      wordBreak: "break-word", // Break long words
                    }}
                  >
                    <RichTextReader
                      html={o.label}
                      customStyles={{
                        div: !formQuestion?.verticalAnswerText ? horizontalStyle : verticalNonsense,
                      }}
                    />
                  </div>
                );
              })}
            </Stack>
          </Grid>

          <Grid item xs={subQuestionSize} />

          <Grid item xs={remainingColumns / 2}>
            <Stack direction="row" spacing="sm">
              {options.map((o, i) => {
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "end",
                      hyphens: "auto",
                      overflow: "hidden", // Ensure text doesn't overflow
                      wordBreak: "break-word", // Break long words
                    }}
                  >
                    <RichTextReader
                      html={o.label}
                      customStyles={{
                        div: !formQuestion?.verticalAnswerText ? horizontalStyle : verticalNonsense,
                      }}
                    />
                  </div>
                );
              })}
            </Stack>
          </Grid>
          {/* End Labels */}

          {subQuestions.filter((sq) => !sq.branchedFromSubQuestionId).map((sq, i) => renderRow(sq, i))}
        </Grid>
      </FormControl>
    );
  };

  return <>{renderSurveyDesktopView()}</>;
};
